package gr.tech.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.icons.fa.FaUser
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.icons.fa.IconStyle
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.styles.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.*

data class FeaturedPerson(
    val name: String,
    val title: String,
    val bio: String,
    val imageUrl: String,
)

data class TeamMember(
    val name: String,
    val position: String,
    val imageUrl: String
)

private val FEATURED_PERSONS = listOf(
    //https://www.businesswire.com/news/home/20241204767317/en/Green-Rebates-Acquires-Seinergy-to-Expand-Rebate-Services-for-Growers?utm_source=chatgpt.com
    FeaturedPerson(
        name = "Dylan Collins",
        title = "Chief Executive Officer",
        bio = "Dylan Collins is the CEO of Green Rebates, where he leads the company’s efforts to help commercial cultivators navigate complex energy rebate and incentive programs. Under his leadership, Green Rebates has expanded its utility partnerships and secured significant rebate value for growers across North America, helping them maximize returns on energy‑efficient upgrades.",
        imageUrl = ImageRepo.DylanCollins1.src,
    ),

    FeaturedPerson(
        name = "Ethan Cohen",
        title = "Creative Producer",
        bio = "Ethan Cohen, AKA \'FreeMoney,\' is Green Rebates’ Creative Producer, responsible for all content from blogs to social media. His energetic presence drives the company’s voice, energizes the community, and gets people excited about the work the team does and the cash they put back into growers’ pockets.",
        imageUrl = ImageRepo.EthanCohen1.src,
    )
)

private val TEAM_MEMBERS = listOf(
    TeamMember("Hannah Massey", "Director of Rebates", "/team/member1.webp"),
    TeamMember("Justin Hall", "VP of Business Development", "/team/member4.webp"),
    TeamMember("Cristina Zamora", "Director of Finance & HR", "/team/member2.webp"),
    TeamMember("Matt Price", "VP of Operations", "/team/member3.webp"),
    TeamMember("Lucas Collins", "Operations Management", "/team/member3.webp"),
    TeamMember("Owen Peters", "Web Developer", "/team/member3.webp"),
)

@InitRoute
fun initMeetTheTeamPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Meet the Green Rebates Team",
        description = "Get to know the team behind Green Rebates — the experts helping commercial growers access energy rebates, LED incentives, and HVAC programs.",
        canonicalUrl = "https://greenrebates.com/meet-the-team"
    ))
}

@Page("meet-the-team")
@Layout(".components.layouts.PageLayout")
@Composable
fun MeetTheTeamPage() {
    Column(
        modifier = Modifier
            .wideCappedWidth()
            .classNames("my-5", "mx-auto"),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        H1(
            Modifier.classNames("fs-1", "fw-bold", "w-100", "text-center", "mb-2")
                .color(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Text("Meet The Team")
        }

        P(
            Modifier.classNames("fs-5", "text-center", "mb-4", "w-75")
                .color(Theme.DarkGreen.rgb)
                .opacity(0.8)
                .toAttrs()
        ) {
            Text("Meet the talented people behind Green Rebates. Dedicated to helping growers lower costs and maximize energy incentives.")
        }

        // Featured persons

        Div(
            attrs = Modifier
                .fillMaxWidth()
                .classNames("d-flex", "flex-column", "gap-4")
                .toAttrs()
        ) {
            FEATURED_PERSONS.forEach { person ->
                FeaturedPersonCard(person)
            }
        }

        // Other Members Team carousel

        H2(
            Modifier.classNames("fs-5", "fw-bold", "w-100", "text-center", "my-3")
                .color(Theme.DarkGreen.rgb).opacity(75.percent)
                .toAttrs()
        ) {
            Text("Other Notable Team Members")
        }

        TeamCarousel()
    }
}

// Composables

@Composable
fun FeaturedPersonCard(person: FeaturedPerson) {
//    Link(
//        path = SitePage.MeetTheTeam.entryPerson(person.name),
//        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
//        modifier = FeaturedPersonCardStyle.toModifier()
//            .classNames("d-flex", "flex-column", "flex-md-row", "align-items-start", "gap-4")
//    ) {
    Div(
        attrs = FeaturedPersonCardStyle.toModifier().classNames("d-flex", "flex-column", "flex-md-row", "align-items-start", "gap-4").toAttrs()
    ) {
        // Headshot
        Img(
            src = person.imageUrl,
            alt = "Photo of ${person.name}",
            attrs = FeaturedPersonImageStyle.toModifier()
                .classNames("flex-shrink-0")
                .toAttrs()
        )

        // Text content
        Div(attrs = Modifier.classNames("d-flex", "flex-column", "gap-2").toAttrs()) {
            H2(
                Modifier.classNames("fs-3", "fw-bold", "mb-1")
                    .color(Theme.DarkGreen.rgb)
                    .toAttrs()
            ) {
                Text(person.name)
            }

            P(
                Modifier.classNames("fs-5", "fw-medium", "mb-2")
                    .color(Theme.MediumGreen.rgb)
                    .toAttrs()
            ) {
                Text(person.title)
            }

            P(
                Modifier.classNames("fs-6", "lh-base", "mb-0")
                    .color(Theme.DarkGreen.rgb)
                    .opacity(0.8)
                    .toAttrs()
            ) {
                Text(person.bio)
            }

            // Span to Visit TODO: Finish once able to start on individual pages
//            SpanText(
//                text = "Learn more about them \u2192",
//                modifier = Modifier.color(Theme.MediumGreen.rgb).classNames("mt-2")
//            )
        }
    }
}

@Composable
fun TeamMemberCard(member: TeamMember) {
    Div(
        attrs = TeamMemberCardStyle.toModifier()
            .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-2", "h-100")
            .toAttrs()
    ) {
//        Img(
//            src = member.imageUrl,
//            alt = "Photo of ${member.name}",
//            attrs = TeamMemberHeadshotStyle.toModifier()
//                .classNames("mb-2")
//                .toAttrs()
//        )
        Div(attrs = TeamMemberHeadshotStyle.toModifier()
            .classNames("d-flex", "justify-content-center", "align-items-center").toAttrs()
        ) {
            FaUser(
                modifier = Modifier.color(Theme.MediumGreen.rgb).scale(1.5),
                style = IconStyle.FILLED,
                size = IconSize.XXL
            )
        }

        H3(
            Modifier.classNames("fs-5", "fw-bold", "mb-0")
                .color(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Text(member.name)
        }

        P(
            Modifier.classNames("fs-6", "mb-0")
                .color(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Text(member.position)
        }
    }
}

@Composable
fun TeamCarousel() {
    var index by remember { mutableStateOf(0) }
    // maxIndex lets prev/next reach every member while keeping 3 visible on desktop.
    // CSS (TeamSliderStyle) controls card width per breakpoint via --team-card-width.
    val maxIndex = TEAM_MEMBERS.size - 1

    Div(
        attrs = TeamSliderStyle.toModifier()
            .fillMaxWidth()
            .classNames("position-relative", "overflow-hidden", "carousel", "carousel-dark", "mb-5", "pb-3")
            .toAttrs()
    ) {
        // Card track — translated by index * --team-card-width
        Div(
            attrs = Modifier
                .classNames("d-flex", "px-2")
                .styleModifier {
                    property("transform", "translateX(calc(-${index} * var(--team-card-width)))")
                    property("transition", "transform 0.4s ease")
                    property("will-change", "transform")
                }
                .toAttrs()
        ) {
            TEAM_MEMBERS.forEach { member ->
                Div(
                    attrs = Modifier
                        .padding(topBottom = 0.5.cssRem, leftRight = 2.cssRem)
                        .styleModifier {
                            property("flex", "0 0 var(--team-card-width)")
                            property("min-width", "var(--team-card-width)")
                        }
                        .toAttrs()
                ) {
                    TeamMemberCard(member)
                }
            }
        }

        // Prev button — hidden at start
        if (index > 0) {
            Button(attrs = {
                classes("carousel-control-prev")
                attr("type", "button")
                attr("aria-label", "Previous")
                onClick { index-- }
            }) {
                Span(attrs = { classes("carousel-control-prev-icon"); attr("aria-hidden", "true") }) {}
                Span(attrs = { classes("visually-hidden") }) { Text("Previous") }
            }
        }

        // Next button — hidden at end
        if (index < maxIndex) {
            Button(attrs = {
                classes("carousel-control-next")
                attr("type", "button")
                attr("aria-label", "Next")
                onClick { index++ }
            }) {
                Span(attrs = { classes("carousel-control-next-icon"); attr("aria-hidden", "true") }) {}
                Span(attrs = { classes("visually-hidden") }) { Text("Next") }
            }
        }
    }
}
